import React, { useContext, useEffect, useState } from 'react'
import { api } from '../../utils'
import { Link, useHistory } from 'react-router-dom'
import ReactPaginate from 'react-paginate'
import { useWindowSize } from '../../hooks'
import { Button, Card, CardHeader, Col, Row, Table } from 'reactstrap'
import { ToastContainer } from 'react-toastify'
import { volumeReportFilterDataContext } from '../../HOC/volumeReportFiltersContext'
import SetTitle from '../../utils/set_title'
import { notify } from '../../utils/notify'
import VolumeRetrieveHistoricalRecovery from './VolumeRetrieveHistoricalRecovery'

function getBigQueryTableUrl(tableReference) {
  if (!tableReference || typeof tableReference !== 'string') {
    return null
  }

  const [project, dataset, ...tableParts] = tableReference.split('.')
  if (!project || !dataset || tableParts.length === 0) {
    return null
  }

  const table = tableParts.join('.')
  return `https://console.cloud.google.com/bigquery?project=${encodeURIComponent(project)}&p=${encodeURIComponent(
    project
  )}&d=${encodeURIComponent(dataset)}&t=${encodeURIComponent(table)}&page=table`
}

function VolumeRetrieveListing() {
  SetTitle('Market Screener')

  const [volumeReportScheduled, setVolumeReportScheduled] = useState([])
  const [pages, setPages] = useState(0)
  const [selectedSchedule, setSelectedSchedule] = useState(null)
  const history = useHistory()
  const windowSize = useWindowSize()
  const itemPerPage = 10

  const [filteredData, , getFilteredData, queryParams, actualPage, setActualPage] = useContext(
    volumeReportFilterDataContext
  )

  async function getData(page = actualPage) {
    api
      .get('volume-report-scheduled/', {
        params: {
          page_size: itemPerPage,
          page,
        },
      })
      .then((response) => {
        setVolumeReportScheduled(response.data.results)
        setPages(Math.ceil(response.data.count / itemPerPage))
      })
  }

  const handleSoftDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo report?')) {
      return
    }

    try {
      await api.post(`volume-report-scheduled/${id}/soft-delete/`)
      setVolumeReportScheduled((prev) => prev.filter((item) => item.id !== id))
      notify('Report eliminato con successo.', 'success')
    } catch (error) {
      notify(error?.response?.data?.error || 'Impossibile eliminare il report.', 'error')
    }
  }

  const handlePageClick = (data) => {
    const currentPage = data.selected + 1
    setActualPage(currentPage)

    if (Object.keys(filteredData).length > 0) {
      getFilteredData({
        ...queryParams,
        page_size: itemPerPage,
        page: currentPage,
      })
      return
    }

    getData(currentPage)
  }

  useEffect(() => {
    if (filteredData.results) {
      setVolumeReportScheduled(filteredData.results)
      setPages(Math.ceil(filteredData.count / itemPerPage))
      return
    }

    setPages(0)
    getData()
  }, [filteredData])

  const icon = <i className="fa-solid fa-plus"></i>
  const scheduleTableMinWidth = '1450px'
  const centeredHeaderStyle = { fontSize: '12px', textAlign: 'center', minWidth: '120px' }
  const ellipsisCellStyle = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  }
  const ellipsisLinkStyle = {
    ...ellipsisCellStyle,
    display: 'block',
  }
  const actionButtonStyle = { fontSize: windowSize.width <= 1280 ? '12px' : '13px' }

  return (
    <>
      <VolumeRetrieveHistoricalRecovery
        isOpen={!!selectedSchedule}
        onClose={() => setSelectedSchedule(null)}
        scheduleId={selectedSchedule?.id}
        initialDetails={selectedSchedule}
      />
      <Row style={{ marginBottom: '100px' }}>
        <div className="col pb-5">
          <Card className="shadow">
            <CardHeader className="border-0">
              <Row style={{ marginLeft: '0px' }}>
                <Col md={10}>
                  <h1 className="mb-0">Market Screener</h1>
                </Col>
                <Col md={2} style={{ textAlign: 'right' }}>
                  <Button
                    style={{ marginRight: '55px' }}
                    color="info"
                    onClick={() => history.push('create-market-screener')}
                    icon={icon}
                  >
                    + Add new
                  </Button>
                </Col>
              </Row>
            </CardHeader>
            <Table
              className="align-items-center table-flush"
              responsive
              style={{ minWidth: scheduleTableMinWidth }}
            >
              <thead className="thead-light">
                <tr>
                  <th scope="col" style={{ fontSize: '12px', minWidth: '230px' }}>
                    Name
                  </th>
                  <th
                    scope="col"
                    style={{ fontSize: '12px', minWidth: '180px', whiteSpace: 'nowrap' }}
                  >
                    Cliente
                  </th>
                  <th scope="col" style={centeredHeaderStyle}>
                    Number of Runs
                  </th>
                  <th scope="col" style={{ ...centeredHeaderStyle, minWidth: '130px' }}>
                    Scheduled Left
                  </th>
                  <th
                    scope="col"
                    style={{ fontSize: '12px', minWidth: '130px', whiteSpace: 'nowrap' }}
                  >
                    Last Run
                  </th>
                  <th
                    scope="col"
                    style={{ fontSize: '12px', minWidth: '120px', whiteSpace: 'nowrap' }}
                  >
                    Country
                  </th>
                  <th
                    scope="col"
                    style={{ fontSize: '12px', minWidth: '260px', whiteSpace: 'nowrap' }}
                  >
                    BigQuery table
                  </th>
                  <th scope="col" style={{ ...centeredHeaderStyle, minWidth: '110px' }}>
                    Reports
                  </th>
                  <th
                    scope="col"
                    style={{ fontSize: '12px', textAlign: 'center', minWidth: '320px' }}
                  ></th>
                </tr>
              </thead>
              <tbody>
                {volumeReportScheduled.map((item) => {
                  const bqTableUrl = getBigQueryTableUrl(item.bq_table_name)

                  return (
                    <tr key={item.id}>
                      <td style={ellipsisCellStyle} title={item.name}>
                        {item.name}
                      </td>
                      <td style={ellipsisCellStyle} title={item.cliente?.nome || '-'}>
                        {item.cliente?.nome || '-'}
                      </td>
                      <td style={{ textAlign: 'center' }}>{item.number_of_runs}</td>
                      <td style={{ textAlign: 'center' }}>{item.remaining_scheduled_executions}</td>
                      <td style={ellipsisCellStyle} title={item.last_run || '-'}>
                        {item.last_run}
                      </td>
                      <td style={ellipsisCellStyle} title={item.locale || '-'}>
                        {item.locale}
                      </td>
                      <td style={ellipsisCellStyle} title={item.bq_table_name}>
                        {bqTableUrl ? (
                          <a
                            href={bqTableUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={ellipsisLinkStyle}
                          >
                            {item.bq_table_name}
                          </a>
                        ) : (
                          item.bq_table_name || '-'
                        )}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <Button
                          size="sm"
                          style={actionButtonStyle}
                          onClick={() => history.push(`/market-screener/executions/${item.id}`)}
                        >
                          Executions
                        </Button>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <div
                          className="d-inline-flex align-items-center justify-content-center"
                          style={{ gap: '8px', flexWrap: 'wrap' }}
                        >
                          <Button
                            color="info"
                            size="sm"
                            style={actionButtonStyle}
                            onClick={() => setSelectedSchedule(item)}
                          >
                            Historical Recovery
                          </Button>
                          <Link to={`/market-screener/edit/${item.id}`}>
                            <Button color="primary" size="sm" style={actionButtonStyle}>
                              Edit
                            </Button>
                          </Link>
                          <Button
                            color="danger"
                            size="sm"
                            style={actionButtonStyle}
                            onClick={() => handleSoftDelete(item.id)}
                          >
                            Elimina
                          </Button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
            {pages > 0 ? (
              <ReactPaginate
                previousLabel={'<'}
                nextLabel={'>'}
                breakLabel={'...'}
                pageCount={pages}
                forcePage={actualPage - 1}
                onPageChange={handlePageClick}
                containerClassName={'pagination justify-content-center pt-5 pb-3'}
                pageClassName={'page-item'}
                pageLinkClassName={'page-link'}
                previousClassName={'page-item'}
                previousLinkClassName={'page-link'}
                nextClassName={'page-item'}
                nextLinkClassName={'page-link'}
                breakClassName={'page-item'}
                breakLinkClassName={'page-link'}
                activeClassName={'active'}
              />
            ) : null}
            <ToastContainer />
          </Card>
        </div>
      </Row>
    </>
  )
}

export default VolumeRetrieveListing
